import { EEthereumNetworkId } from '@ankr.com/provider';
import axios, { AxiosInstance } from 'axios';
import BigNumber from 'bignumber.js';
import config, { NetworkConfig } from 'config';
import { parseISO } from 'date-fns';
import { AbiItem } from 'web3-utils';

import { convertFromWei } from '../core';
import { AXIOS_DEFAULT_CONFIG, ErrorType } from './consts';
import {
  ApiTask,
  ApiTaskExecution,
  ApiTaskExecutionResponse,
  ApiTaskResponse,
  GetTokenPriceResponse,
} from './types';
import { convertApiTaskResponse } from './utils';

export class AutomateApi {
  private api: AxiosInstance;

  private networkConfig: NetworkConfig;

  public constructor(chainId: EEthereumNetworkId) {
    const networkConfig = config[chainId];
    if (!networkConfig) {
      throw new Error(ErrorType.ConfigNotFound);
    }

    this.networkConfig = networkConfig;
    this.api = axios.create({
      ...AXIOS_DEFAULT_CONFIG,
      baseURL: networkConfig.urls.api,
    });
  }

  async getAbi(contractAddress: string): Promise<AbiItem[]> {
    const { data, status } = await this.api.get<AbiItem[]>(
      `/abi/${contractAddress}`,
    );
    if (status !== 200 || !data) {
      throw new Error(ErrorType.ABINotFound);
    }

    return data;
  }

  async uploadAbi(contractAddress: string, abi: AbiItem[]): Promise<void> {
    await this.api.post(`/abi/${contractAddress}`, abi);
  }

  async getTask(taskId: string): Promise<ApiTask> {
    const { data, status } = await this.api.get<ApiTaskResponse>(
      `/upkeeps/${taskId}`,
    );

    if (status !== 200 || !data) {
      throw new Error(ErrorType.TaskNotFound);
    }

    return convertApiTaskResponse(data);
  }

  async getAccountTasks(accountAddress: string): Promise<ApiTask[]> {
    const { data, status } = await this.api.get<ApiTaskResponse[]>(
      `/upkeeps?admin=${accountAddress}`,
    );

    if (status !== 200 || !Array.isArray(data)) {
      throw new Error(ErrorType.TasksNotFound);
    }

    return data.map(task => convertApiTaskResponse(task));
  }

  async getTaskExecutions(taskId: string): Promise<ApiTaskExecution[]> {
    const { data, status } = await this.api.get<ApiTaskExecutionResponse[]>(
      `/upkeeps/${taskId}/executions`,
    );

    if (status !== 200 || !Array.isArray(data)) {
      throw new Error(ErrorType.ExecutionsNotFound);
    }

    return data.map(
      // eslint-disable-next-line camelcase
      ({ upkeep_id, tx_hash, executed_at, payment, success }) => ({
        success,
        taskId: upkeep_id,
        txHash: tx_hash,
        executedAt: parseISO(executed_at),
        payment: convertFromWei(payment),
      }),
    );
  }

  async getNativeTokenUSDPrice(): Promise<BigNumber> {
    const { data, status } = await this.api.get<GetTokenPriceResponse>(`/rate`);

    const usdPrice = data?.usdPrice;

    if (status !== 200 || !usdPrice) {
      throw new Error(ErrorType.USDPriceNotFound);
    }

    return new BigNumber(usdPrice);
  }
}
